import { Link } from '@tanstack/react-router';
import type { CityBroadcastStateDto, EventChatBroadcastDto, EventChatBroadcastStatus } from '@upme/contracts';
import { badgeVariants } from '@upme/ui/components/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@upme/ui/components/tooltip';
import { formatEventDateTime } from '../../events/lib/format';

const STATUS_LABEL: Record<EventChatBroadcastStatus, string> = {
  QUEUED: 'в очереди',
  SENDING: 'отправляется',
  SENT: 'отправлено',
  FAILED: 'ошибка',
  DELETING: 'удаляется',
  DELETED: 'удалено',
  DELETE_FAILED: 'удалите вручную',
};

const STATUS_VARIANT: Record<EventChatBroadcastStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    QUEUED: 'outline',
    SENDING: 'outline',
    SENT: 'default',
    FAILED: 'destructive',
    DELETING: 'outline',
    DELETED: 'secondary',
    DELETE_FAILED: 'destructive',
  };

/**
 * Ошибка, требующая действия админа, видна сразу: FAILED — «проверьте чат» / «не удалось подготовить»,
 * DELETE_FAILED — удалить пост руками. Остальные — в тултипе.
 */
const VISIBLE_ERROR: ReadonlySet<EventChatBroadcastStatus> = new Set(['FAILED', 'DELETE_FAILED']);

function StatusBadge({ status }: { status: EventChatBroadcastStatus }) {
  return <span className={badgeVariants({ variant: STATUS_VARIANT[status] })}>{STATUS_LABEL[status]}</span>;
}

function StatusCell({ row }: { row: EventChatBroadcastDto }) {
  if (!row.error) return <StatusBadge status={row.status} />;
  if (VISIBLE_ERROR.has(row.status)) {
    return (
      <div className="space-y-1">
        <StatusBadge status={row.status} />
        <p className="max-w-sm text-xs text-destructive">{row.error}</p>
      </div>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="cursor-help" aria-label={`Ошибка: ${row.error}`}>
          <StatusBadge status={row.status} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">{row.error}</TooltipContent>
    </Tooltip>
  );
}

function emptyQueueText(state: CityBroadcastStateDto): string {
  // getState считает очередь только при activatedAt != null: до первого включения она всегда пуста.
  return state.activatedAt === null
    ? 'Очередь пуста — в неё попадут события, опубликованные после включения автопостинга'
    : 'Очередь пуста';
}

export function BroadcastQueue({ state }: { state: CityBroadcastStateDto }) {
  const paused = !state.isActive && state.activatedAt !== null;

  return (
    // В приложении TooltipProvider даёт SidebarProvider; свой — чтобы блок работал и вне него (тесты).
    <TooltipProvider>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="font-medium">
            Ближайшие в очереди ({state.queue.count})
            {paused && (
              <span className="ml-1 text-sm font-normal text-muted-foreground">
                — уйдут после включения. Повторное включение того же чата отправит и накопившееся за паузу
              </span>
            )}
          </h3>
          {state.queue.next.length === 0 ? (
            <p className="text-sm text-muted-foreground">{emptyQueueText(state)}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {state.queue.next.map((e) => (
                <li key={e.id} className="flex gap-3">
                  <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                    {formatEventDateTime(e.startsAt, state.timezone, e.hasStartTime)}
                  </span>
                  <Link to="/events/$eventId" params={{ eventId: e.id }} className="hover:underline">
                    {e.title}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="font-medium">Последние отправки</h3>
          {state.recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">Отправок пока не было</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Время</TableHead>
                  <TableHead>Событие</TableHead>
                  <TableHead>Статус</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.recent.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {formatEventDateTime(r.sentAt ?? r.createdAt, state.timezone)}
                    </TableCell>
                    <TableCell>
                      {r.eventId ? (
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: r.eventId }}
                          className="hover:underline"
                        >
                          {r.eventTitle ?? 'без названия'}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">событие удалено</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusCell row={r} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
